import isbot from "isbot";
import {useEffect} from "react";

import {getPublicEnv} from "../../../utils/public-env";
import {getDevice} from "../browser-storage/device";
import {clearEventQueue, getEventQueue} from "../browser-storage/eventQueue";
import {getSessionId} from "../browser-storage/sessionId";
import {getVisitId, getVisitorId} from "../browser-storage/visitData";
import {isCI} from "../env";
import {AppType, CarbonUserType} from "./constants";
import {Event, EventPayload} from "./types";
import {getAnalyticsEndpoint} from "./urls";

const postEventBeacon = (payload: EventPayload) => {
  const endpoint = getAnalyticsEndpoint();
  const wasDataQueueOpened = navigator.sendBeacon(
    endpoint,
    new Blob([JSON.stringify(payload)], {type: "application/json"}),
  );
  if (wasDataQueueOpened) {
    clearEventQueue();
  }
};

const postEvents = (payload: EventPayload): Promise<Response> =>
  fetch(getAnalyticsEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then(response => {
    clearEventQueue();
    return response;
  });

/**
 * Checks required metadata fields to determine if we have all needed data to build event payload
 */
export const isAnalyticsReady = () =>
  !!getSessionId() && !!getDevice() && !!getVisitId() && !!getVisitorId();

export const buildEventPayload = (events: Event[]): EventPayload | null => {
  const device = getDevice();
  const visitId = getVisitId();
  const visitorId = getVisitorId();

  return device && visitId && visitorId && events
    ? {
        events,
        device,
        appInfo: {
          appType: AppType.PublicWebsite,
          appVersion: getPublicEnv().NEXT_PUBLIC_GIT_TAG || "development",
        },
        user: {
          visitId,
          visitorId,
          carbonUserType: CarbonUserType.Unknown,
        },
      }
    : null;
};

export const postQueue = (beacon = false): void => {
  const eventQueue = getEventQueue();
  const payload = eventQueue.length > 0 && buildEventPayload(eventQueue);
  if (payload && (!isbot(navigator.userAgent) || isCI)) {
    if (beacon) {
      postEventBeacon(payload);
    } else {
      postEvents(payload);
    }
  }
};

const POST_EVENT_INTERVAL = 5000;

export const usePostEventQueue = () => {
  useEffect(() => {
    const onVisibilityStateChange = () => {
      if (document.visibilityState === "hidden") {
        postQueue(true);
      }
    };

    postQueue();
    document.addEventListener("visibilitychange", onVisibilityStateChange);
    const interval = setInterval(postQueue, POST_EVENT_INTERVAL);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityStateChange);
    };
  }, []);
};
